import { Provider, inject, signal, computed, makeEnvironmentProviders, EnvironmentProviders } from '@angular/core'
import { firstValueFrom } from 'rxjs'
import {
  TICKET_CONFIG,
  TICKET_CURRENT_USER,
  TICKET_DEVICE_INFO,
  TICKET_DIALOG_SERVICE,
  TICKET_ASSIGNEE_PROVIDER,
  TICKET_ASSIGNEE_PICKER_COMPONENT,
  TICKET_RATE_AI_SUGGESTION,
  TICKET_AI_SOURCE_CLICKED,
  TICKET_REDO_AI_SUGGESTION,
  TICKET_MENTION_CLICKED,
  TicketService,
} from '@solidgroup-agency/ticket-system/services'
import { AiSuggestionRating, AiSourceDocument } from '@solidgroup-agency/ticket-system/models'
import { environment } from '@awo-care/shared-auth'
import { TicketUser, DeviceInfo, TicketSystemConfig } from '@solidgroup-agency/ticket-system/models'
import { UserService } from '../core/services/user.service'
import { DeviceInfoService, KnowledgebaseService, SnackbarService } from '@awo-care/shared-core'
import { KnowledgebaseDialogComponent, KnowledgebaseDialogData } from '@awo-care/shared-ui'
import { DialogService } from '../core/components/dialog/dialog.service'
import { AwoAssigneeProvider } from './awo-assignee.provider'
import { AwoTicketService } from './awo-ticket.service'
import { AwoAssigneePickerComponent } from './awo-assignee-picker.component'
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { FIREBASE_FIRESTORE, FIREBASE_FUNCTIONS } from '@awo-care/shared-firebase';
import { Firestore, doc, getDoc } from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';

const AWO_CATEGORIES = [
  { value: 'general', label: 'Allgemein' },
  { value: 'technical', label: 'Technisch' },
  { value: 'billing', label: 'Abrechnung' },
  { value: 'feature_request', label: 'Feature-Anfrage' },
  { value: 'bug_report', label: 'Fehlerbericht' },
  { value: 'other', label: 'Sonstiges' },
]

const AWO_PRIORITIES = [
  { value: 'critical', label: 'Kritisch', color: '#d32f2f', sortOrder: 0 },
  { value: 'high', label: 'Hoch', color: '#f44336', sortOrder: 1 },
  { value: 'medium', label: 'Mittel', color: '#ff9800', sortOrder: 2 },
  { value: 'low', label: 'Niedrig', color: '#4caf50', sortOrder: 3 },
]

const AWO_STATUSES = [
  { value: 'open', label: 'Offen', color: '#2196f3' },
  { value: 'in_progress', label: 'In Bearbeitung', color: '#ff9800' },
  { value: 'reopened', label: 'Wieder geöffnet', color: '#ff5722' },
  { value: 'resolved', label: 'Gelöst', color: '#4caf50' },
  { value: 'closed', label: 'Geschlossen', color: '#9e9e9e' },
]

export function provideAwoTicketSystemConfig(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: TICKET_CONFIG,
      useFactory: (): TicketSystemConfig => {
        const firestore = inject(FIREBASE_FIRESTORE)
        return {
          firestore,
          collectionPath: 'tickets',
          roles: { canView: ['admin', 'manager'], canAssign: ['admin'] },
          features: {
            // aiPrioritization: false → blendet AI-Suggestion + KI-Priorität-Hint
            // im Ticket-Detail aus. Bei AI-Reaktivierung wieder auf true setzen.
            aiPrioritization: false,
            emailNotifications: true,
            internalNotes: true,
            emailInbound: true,
            contactFormBridge: true,
            unreadTracking: true,
            attachments: true,
          },
          attachments: {
            maxFileSizeMB: 10,
            allowedMimeTypes: ['image/*', 'application/pdf', 'text/plain'],
          },
          categories: AWO_CATEGORIES,
          priorities: AWO_PRIORITIES,
          statuses: AWO_STATUSES,
          labels: {
            ticketSingular: 'Ticket',
            ticketPlural: 'Tickets',
            facilityLabel: 'Schule',
            assigneeLabel: 'Zugewiesen an',
          },
          sortField: 'lastActivityOn',
        }
      },
    },
  ])
}

export function provideAwoTicketSystem(): Provider[] {
  return [
    {
      provide: TICKET_CURRENT_USER,
      useFactory: () => {
        const userService = inject(UserService)
        const firestore = inject(FIREBASE_FIRESTORE)
        const employeeName = signal<string | null>(null)
        let loadedUid: string | null = null

        return computed((): TicketUser | null => {
          const user = userService.currentUser()
          if (!user?.uid) return null

          // Load employee name once per user
          if (loadedUid !== user.uid) {
            loadedUid = user.uid
            getDoc(doc(firestore, 'employees', user.uid)).then((snap) => {
              if (snap.exists()) {
                const data = snap.data() as Record<string, unknown>
                const name = [data['firstName'], data['lastName']].filter(Boolean).join(' ')
                if (name) employeeName.set(name)
                const sig = data['signature']
                // Nicht überschreiben, wenn ein Save die Signatur inzwischen frischer gesetzt hat.
                if (userService.ownSignature() === null && typeof sig === 'string') {
                  userService.ownSignature.set(sig)
                }
              }
            })
          }

          const signature = (userService.ownSignature() ?? '').trim()
          return {
            uid: user.uid,
            email: user.email ?? '',
            displayName: employeeName() || user.displayName || user.email || '',
            // WICHTIG: nur setzen wenn vorhanden — undefined bricht den Firestore-Write des Kommentar-author.
            ...(signature ? { signature } : {}),
          }
        })
      },
    },
    {
      provide: TICKET_DEVICE_INFO,
      useFactory: () => {
        const deviceInfoService = inject(DeviceInfoService)
        const info = deviceInfoService.getDeviceInfo()
        return computed((): DeviceInfo => ({
          os: info?.os || 'unknown',
          browser: info?.browser || 'unknown',
          resolution: info?.screenResolution || 'unknown',
        }))
      },
    },
    {
      provide: TICKET_DIALOG_SERVICE,
      useFactory: () => {
        const dialogService = inject(DialogService)
        const snackbarService = inject(SnackbarService)
        return {
          confirm: (title: string, message: string) => {
            const ref = dialogService.showDialog(title, message)
            return firstValueFrom(ref.afterClosed()).then((res) => !!res)
          },
          snackbar: (msg: string, action?: string, duration?: number) =>
            snackbarService.show(msg, {
              action: action ?? 'OK',
              duration: duration ?? 3000,
            }),
        }
      },
    },
    { provide: TicketService, useExisting: AwoTicketService },
    { provide: TICKET_ASSIGNEE_PROVIDER, useClass: AwoAssigneeProvider },
    { provide: TICKET_ASSIGNEE_PICKER_COMPONENT, useValue: AwoAssigneePickerComponent },
    {
      provide: TICKET_AI_SOURCE_CLICKED,
      useFactory: () => {
        const kbService = inject(KnowledgebaseService)
        const dialog = inject(MatDialog)
        return (source: AiSourceDocument) => {
          const docId = kbService.sourceToDocId(source.title)
          if (docId) {
            dialog.open(KnowledgebaseDialogComponent, {
              data: { docId, highlightText: source.highlightText } as KnowledgebaseDialogData,
              width: '800px',
              maxWidth: '95vw',
            })
          }
        }
      },
    },
    {
      provide: TICKET_RATE_AI_SUGGESTION,
      useFactory: () => {
        const functions = inject(FIREBASE_FUNCTIONS)
        return async (ticketId: string, commentId: string, rating: AiSuggestionRating) => {
          const callable = httpsCallable(functions, 'rateAiSuggestion')
          await callable({ ticketId, commentId, rating })
        }
      },
    },
    {
      provide: TICKET_MENTION_CLICKED,
      useFactory: () => {
        const router = inject(Router)
        return (uid: string) => {
          router.navigate(['/employees/details', uid])
        }
      },
    },
    ...(!environment.production
      ? [
          {
            provide: TICKET_REDO_AI_SUGGESTION,
            useFactory: () => {
              const functions = inject(FIREBASE_FUNCTIONS)
              return async (ticketId: string) => {
                const callable = httpsCallable(functions, 'redoAiSuggestion')
                await callable({ ticketId })
              }
            },
          },
        ]
      : []),
  ]
}
